import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
import { DeviceAssociatedUsersList } from "./DeviceAssociatedUsersList";

interface Device {
  deviceId: string;
  deviceName: string;
  associatedUsers: Array<{
    userId: string;
    firstName: string;
    lastName: string;
    email?: string;
    identity: string;
  }>;
}

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  devices: Device[];
  onConfirmDelete: () => Promise<void>;
  isDeleting: boolean;
}

export const DeleteUserDialog = ({
  open,
  onOpenChange,
  userName,
  devices,
  onConfirmDelete,
  isDeleting,
}: DeleteUserDialogProps) => {
  const totalAssociatedUsers = devices.reduce(
    (sum, device) => sum + device.associatedUsers.length,
    0
  );

  const handleDelete = async () => {
    await onConfirmDelete();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>Delete User</DialogTitle>
          </div>
          <DialogDescription className="text-left pt-2">
            Are you sure you want to delete <span className="font-semibold">{userName}</span>?
            {devices.length > 0 && (
              <span className="block mt-2 text-destructive">
                The following {devices.length} device{devices.length !== 1 ? 's' : ''} 
                {totalAssociatedUsers > 0 && ` and ${totalAssociatedUsers} associated user${totalAssociatedUsers !== 1 ? 's' : ''}`} will also be deleted:
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <DeviceAssociatedUsersList devices={devices} />

        <DialogFooter className="flex-row gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Anyway"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
